import type { PrefetchContext, PrefetchedDataProps } from '#src/shared/prefetch';

import { useCountryCode, useLocale } from '#src/app/shared/hooks/l10n';
import { useQueryString } from '#src/app/shared/hooks/location';
import type { MergedState } from '#src/app/shared/hooks/state';
import { useMergedStates } from '#src/app/shared/hooks/state';
import { getQueryStringSingleValue } from '#src/app/shared/utils/query';

import { prefetchPsfPromoteOrSharePageDataFromApi, usePsfPromoteOrSharePageDataFromApi } from './pageData';

export type PsfPromoteOrSharePagePrefetchedContext = Readonly<{
	pageData: NonNullable<Parameters<typeof usePsfPromoteOrSharePageDataFromApi>[0]>;
}>;

export type PsfPromoteOrSharePageCombinedState = MergedState<ReturnType<typeof usePsfPromoteOrSharePageDataFromApi>>;

export function usePageContext(
	slug: string,
	prefetchedData?: PsfPromoteOrSharePagePrefetchedContext,
): PsfPromoteOrSharePageCombinedState {
	const query = useQueryString();
	const countryCode = useCountryCode();
	const locale = useLocale();

	return useMergedStates(
		usePsfPromoteOrSharePageDataFromApi(
			prefetchedData?.pageData,
			{ slug, locale, countryCode },
			{
				recruiterId: getQueryStringSingleValue(query, 'recruiter'),
				banditOverrides: {
					combo: getQueryStringSingleValue(query, 'bo.combo'),
				},
			},
		),
	);
}

export async function prefetchPageContext(
	slug: string,
	context: PrefetchContext,
): Promise<PsfPromoteOrSharePagePrefetchedContext> {
	const {
		l10n: { countryCode, locale },
	} = context;
	const [pageData] = await Promise.all([
		prefetchPsfPromoteOrSharePageDataFromApi(
			context,
			{ slug, locale, countryCode },
			{
				recruiterId: getQueryStringSingleValue(context.query, 'recruiter'),
				banditOverrides: {
					combo: getQueryStringSingleValue(context.query, 'bo.combo'),
				},
			},
		),
	]);

	return { pageData };
}

export type PsfPromoteOrSharePagePrefetchedDataProps = PrefetchedDataProps<PsfPromoteOrSharePagePrefetchedContext>;
